import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useSelector } from 'react-redux';
import { throttleRAF } from '../../../utils/throttle';

// Helper: Scene içinde recursive olarak obje bul
const findObjectInScene = (scene, predicate) => {
  let result = null;
  scene.traverse(obj => {
    if (!result && predicate(obj)) {
      result = obj;
    }
  });
  return result;
};

export default function useLightingGizmoEvents({ scene, camera, renderer }) {
  const rotatingLightSwitchRef = useRef(null);
  const stretchingLightSwitchRef = useRef(null);
  const startAngleRef = useRef(0);
  const startRotationZRef = useRef(0);
  const startStretchYRef = useRef(0);
  const startMouseYRef = useRef(0);
  const grabOffsetRef = useRef(new THREE.Vector3());

  const orthoMode = useSelector((state) => state.mods.orthoMode);

  useEffect(() => {
    if (!scene || !camera || !renderer) return;

    const dom = renderer.domElement;
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const getWorldPoint = (event) => {
      const rect = dom.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);

      const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
      const point = new THREE.Vector3();
      raycaster.ray.intersectPlane(plane, point);
      return point;
    };


    const handleMouseDown = (event) => {
      const point = getWorldPoint(event);
      const intersects = raycaster.intersectObjects(scene.children, true);


      const rotateHit = intersects.find(i =>
        i.object?.userData?.type === 'rotateGizmo' &&
        i.object?.userData?.parentLightSwitchId // Sadece LightSwitch gizmo'larını yakala
      );
      if (rotateHit) {
        const parentId = rotateHit.object.userData.parentLightSwitchId;
        

        const lightSwitchGroup = findObjectInScene(scene, obj => obj.userData?.id === parentId && obj.userData?.type === 'LightSwitch');

        if (!lightSwitchGroup) {
          console.error('❌ lightSwitchGroup bulunamadı! Aranan parentId:', parentId);
          return;
        }

        

        // offsetGroup'u userData.isOffsetGroup ile bul
        const offsetGroup = lightSwitchGroup?.children.find(child => child.userData?.isOffsetGroup === true);

        if (!offsetGroup) {
          console.warn('⚠️ offsetGroup bulunamadı! lightSwitchGroup children:', lightSwitchGroup.children.length);
          lightSwitchGroup.children.forEach((child, idx) => {
            
          });
          return;
        }

        

        // lightSwitch fpMesh-child olabilir (mesh-FP), .position LOCAL döner —
        // point WORLD olduğundan açı için dünya konumu gerekli.
        const center = lightSwitchGroup.getWorldPosition(new THREE.Vector3());
        const startVector = new THREE.Vector2(point.x - center.x, point.y - center.y);
        startAngleRef.current = Math.atan2(startVector.y, startVector.x);
        startRotationZRef.current = offsetGroup.rotation.z;
        rotatingLightSwitchRef.current = offsetGroup;
        return;
      }

      const stretchHit = intersects.find(i => i.object?.userData?.type === 'stretchGizmo');
      if (stretchHit) {
        const parentId = stretchHit.object.userData.parentLightSwitchId;
        

        const lightSwitchGroup = findObjectInScene(scene, obj => obj.userData?.id === parentId && obj.userData?.type === 'LightSwitch');
        // offsetGroup'u userData.isOffsetGroup ile bul
        const offsetGroup = lightSwitchGroup?.children.find(child => child.userData?.isOffsetGroup === true);
        if (!offsetGroup) {
          console.warn('⚠️ Stretch: offsetGroup bulunamadı!');
          return;
        }

        const initialStretch =
          offsetGroup.userData?.stretchLength ??
          lightSwitchGroup.userData?.stretchLength ??
          0;

        startStretchYRef.current = initialStretch;
        startMouseYRef.current = point.y;
        stretchingLightSwitchRef.current = offsetGroup;

        
        return;
      }
    };

    const handleMouseMove = (event) => {
      const point = getWorldPoint(event);

      if (rotatingLightSwitchRef.current) {
        // lightSwitch fpMesh-child olabilir — parent.position LOCAL; point WORLD.
        const center = rotatingLightSwitchRef.current.parent.getWorldPosition(new THREE.Vector3());
        const currentVector = new THREE.Vector2(point.x - center.x, point.y - center.y);
        let currentAngle = Math.atan2(currentVector.y, currentVector.x);

        if (orthoMode) {
          const rawDelta = currentAngle - startAngleRef.current;
          const deltaDeg = THREE.MathUtils.radToDeg(rawDelta);
          const snappedDeg = Math.round(deltaDeg / 45) * 45;
          currentAngle = startAngleRef.current + THREE.MathUtils.degToRad(snappedDeg);

        }

        const delta = currentAngle - startAngleRef.current;
        rotatingLightSwitchRef.current.rotation.z = startRotationZRef.current + delta;
        // Yeni toplam açıyı derece cinsinden userData'ya kaydet
        const totalAngleDegrees = (startRotationZRef.current + delta) * 180 / Math.PI;
        rotatingLightSwitchRef.current.parent.userData.rotate = totalAngleDegrees;
      }
      const gridSize = 5; // Stretch adımı

      if (stretchingLightSwitchRef.current) {
        const offsetGroup = stretchingLightSwitchRef.current;
        const lightSwitchGroup = offsetGroup.parent;

        let deltaY = point.y - startMouseYRef.current;

        // Dönme açısını normalize et (-π ~ π aralığına)
        let angle = stretchingLightSwitchRef.current.rotation.z % (2 * Math.PI);
        if (angle < 0) angle += 2 * Math.PI;

        // 90° ile 270° arası ters yön (yani aşağıyı gösteriyorsa ters çevir)
        const isFlipped = angle > Math.PI / 2 && angle < (3 * Math.PI) / 2;
        if (isFlipped) deltaY = -deltaY;

        // Kademeli yap: 5 birim adımlarla yuvarla
        const steppedDeltaY = Math.round(deltaY / gridSize) * gridSize;
        const clampedDeltaY = Math.max(0, steppedDeltaY);

        // ✅ Type bilgisini al (normal, Komütatör, vaviyen, etanj)
        const switchType = lightSwitchGroup.userData?.subtype || 'normal';

        // === line1 (LS_line1) güncelle ===
        const line1 = stretchingLightSwitchRef.current.children.find(
          (child) => child.userData?.role === 'LS_line1'
        );
        if (line1) {
          const newLength = Math.max(15, 15 + clampedDeltaY);
          const points = [new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, newLength, 0)];
          line1.geometry.setFromPoints(points);
        }

        // === circle güncelle (geometry yeniden yarat) ===
        const circle = stretchingLightSwitchRef.current.children.find(
          (child) => child.userData?.role === 'LS_circle'
        );
        if (circle) {
          // Circle geometry'sini yeniden yarat (merkezi 21 + clampedDeltaY'de)
          const circleRadius = 6;
          const circleCenter = new THREE.Vector3(0, 21 + clampedDeltaY, 0);
          const circlePoints = [];
          const segments = 32;

          for (let i = 0; i <= segments; i++) {
            const angle = (i / segments) * Math.PI * 2;
            const x = circleCenter.x + circleRadius * Math.cos(angle);
            const y = circleCenter.y + circleRadius * Math.sin(angle);
            circlePoints.push(new THREE.Vector3(x, y, 0));
          }

          circle.geometry.dispose();
          circle.geometry = new THREE.BufferGeometry().setFromPoints(circlePoints);
          // Position'ı sıfırla çünkü geometry zaten doğru yerde
          circle.position.set(0, 0, 0);
        }

        // === line2 (LS_line2) ve line3 (LS_line3) güncelle - TYPE'a göre ===
        const line2 = stretchingLightSwitchRef.current.children.find(
          (child) => child.userData?.role === 'LS_line2'
        );
        const line3 = stretchingLightSwitchRef.current.children.find(
          (child) => child.userData?.role === 'LS_line3'
        );

        if (switchType.includes('normal')) {
          // Normal tip
          if (line2) {
            const newPoints = [
              new THREE.Vector3(0, 27 + clampedDeltaY, 0),
              new THREE.Vector3(0, 42 + clampedDeltaY, 0),
              new THREE.Vector3(5, 42 + clampedDeltaY, 0)
            ];
            line2.geometry.setFromPoints(newPoints);
          }
        } else if (switchType.includes('Komütatör')) {
          // Komütatör tip
          if (line2) {
            const newPoints = [
              new THREE.Vector3(2.92, 26.6 + clampedDeltaY, 0),
              new THREE.Vector3(10.7, 39.11 + clampedDeltaY, 0),
              new THREE.Vector3(15.02, 36.58 + clampedDeltaY, 0)
            ];
            line2.geometry.setFromPoints(newPoints);
          }
          if (line3) {
            const newPoints = [
              new THREE.Vector3(-2.92, 26.6 + clampedDeltaY, 0),
              new THREE.Vector3(-10.7, 39.11 + clampedDeltaY, 0),
              new THREE.Vector3(-15.02, 36.58 + clampedDeltaY, 0)
            ];
            line3.geometry.setFromPoints(newPoints);
          }
        } else if (switchType === 'vaviyen') {
          // Vaviyen tip
          if (line2) {
            const newPoints = [
              new THREE.Vector3(2.92, 26.6 + clampedDeltaY, 0),
              new THREE.Vector3(10.7, 39.11 + clampedDeltaY, 0),
              new THREE.Vector3(15.02, 36.58 + clampedDeltaY, 0)
            ];
            line2.geometry.setFromPoints(newPoints);
          }
          if (line3) {
            const newPoints = [
              new THREE.Vector3(-2.92, 26.6 + clampedDeltaY, 0),
              new THREE.Vector3(-10.7, 39.11 + clampedDeltaY, 0),
              new THREE.Vector3(-6.07, 41.69 + clampedDeltaY, 0)
            ];
            line3.geometry.setFromPoints(newPoints);
          }
        }

        // === etanjFill varsa güncelle ===
        const etanjFill = stretchingLightSwitchRef.current.children.find(
          (child) => child.userData?.role === 'etanjFill'
        );
        if (etanjFill) {
          // etanjFill geometry 0'da, position ile taşınıyor
          etanjFill.position.y = 21 + clampedDeltaY;
        }

        // === rotateGizmo ===
        const rotateGizmo = stretchingLightSwitchRef.current.children.find(
          (child) => child.userData?.type === 'rotateGizmo'
        );
        if (rotateGizmo) {
          rotateGizmo.position.y = 33 + clampedDeltaY;
        }

        // === stretchGizmo ===
        const stretchGizmo = stretchingLightSwitchRef.current.children.find(
          (child) => child.userData?.type === 'stretchGizmo'
        );
        if (stretchGizmo) {
          stretchGizmo.position.y = clampedDeltaY;
        }

        // === symetricGizmo ===
        const symetricGizmo = stretchingLightSwitchRef.current.children.find(
          (child) => child.userData?.type === 'symetricGizmo'
        );
        if (symetricGizmo) {
          symetricGizmo.position.y = clampedDeltaY;
        }
        if (lightSwitchGroup) {
          lightSwitchGroup.userData.stretchLength = clampedDeltaY;
        }
        offsetGroup.userData.stretchLength = clampedDeltaY;

      }
    };

    const handleMouseUp = () => {
      rotatingLightSwitchRef.current = null;
      stretchingLightSwitchRef.current = null;
    };

    const handleClick = (event) => {
      const point = getWorldPoint(event);
      const intersects = raycaster.intersectObjects(scene.children, true);
      const hit = intersects.find(i => i.object?.userData?.type === 'symetricGizmo');
      if (!hit) return;

      const parentId = hit.object.userData.parentLightSwitchId;
      const lightSwitchGroup = findObjectInScene(scene, obj =>
        obj.userData?.id === parentId && obj.userData?.type === 'LightSwitch'
      );

      if (lightSwitchGroup && lightSwitchGroup.children.length > 0) {
        // offsetGroup'u userData.isOffsetGroup ile bul
        const offsetGroup = lightSwitchGroup.children.find(child => child.userData?.isOffsetGroup === true);
        if (offsetGroup) {
          // 180 derece dönüş (Math.PI radians)
          offsetGroup.rotation.z += Math.PI;
          // userData.rotate'i de güncelle (degrees)
          const newAngleDegrees = (offsetGroup.rotation.z * 180 / Math.PI) % 360;
          lightSwitchGroup.userData.rotate = newAngleDegrees;
        } else {
          console.warn('⚠️ Symetric: offsetGroup bulunamadı!');
        }
      }
    };

    const throttledMouseMove = throttleRAF(handleMouseMove);

    dom.addEventListener('mousedown', handleMouseDown);
    dom.addEventListener('mousemove', throttledMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    dom.addEventListener('click', handleClick);

    return () => {
      throttledMouseMove.cancel();
      dom.removeEventListener('mousedown', handleMouseDown);
      dom.removeEventListener('mousemove', throttledMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      dom.removeEventListener('click', handleClick);
    };
  }, [scene, camera, renderer, orthoMode]);
}
